'use client'

import { useEffect, useRef } from 'react'

import { useGateValue } from '~/configs/statsig'
import { useGetOrg } from '~/data/api/hooks/organizations'
import { useCloakingInfo, useCurrentOrgId } from '~/store/slices/currentOrg'

import stripOrgDocumentTitleSuffix, { sanitizeDocumentTitle } from './orgDocumentTitle'

/**
 * When the org hierarchy bar gate is on, appends " · {Current org}" to
 * `document.title` so cloaked tabs are distinguishable. Mount only inside the
 * authenticated layout — not from `usePageTitle`, which must stay provider-free.
 *
 * Strips only the org suffix this component last applied (compared after the
 * same whitespace sanitize browsers apply to `document.title`). Never strip
 * from the first ` · ` — page titles may contain middots.
 */
export default function OrgDocumentTitleSuffix() {
  const hierarchyBarEnabled = useGateValue('org_hierarchy_bar')
  const currentOrgId = useCurrentOrgId()
  const { cloakedOrgs } = useCloakingInfo()
  const { org } = useGetOrg(hierarchyBarEnabled ? currentOrgId : undefined)
  const orgName = sanitizeDocumentTitle(org?.organizationNm || cloakedOrgs.at(-1)?.name || '')
  const suffix = hierarchyBarEnabled && orgName ? ` · ${orgName}` : ''
  const suffixRef = useRef(suffix)
  const appliedSuffixRef = useRef('')
  suffixRef.current = suffix

  useEffect(() => {
    const clearAppliedSuffix = () => {
      const stripped = stripOrgDocumentTitleSuffix(document.title, appliedSuffixRef.current, '')
      if (sanitizeDocumentTitle(document.title) !== stripped) {
        document.title = stripped
      }
      appliedSuffixRef.current = ''
    }

    if (!suffix) {
      clearAppliedSuffix()
      return
    }

    const apply = () => {
      const desired = suffixRef.current
      if (!desired) return

      const base = stripOrgDocumentTitleSuffix(
        document.title,
        appliedSuffixRef.current,
        desired
      )
      const next = sanitizeDocumentTitle(`${base}${desired}`)
      if (sanitizeDocumentTitle(document.title) !== next) {
        document.title = next
      }
      appliedSuffixRef.current = desired
    }

    apply()

    const observer = new MutationObserver(apply)
    observer.observe(document.head, { childList: true, characterData: true, subtree: true })
    // Strip on unmount / suffix change so the org label cannot linger after the
    // gate turns off or the authenticated layout unmounts (usePageTitle would
    // otherwise keep treating `base · org` as already correct).
    return () => {
      observer.disconnect()
      clearAppliedSuffix()
    }
  }, [suffix])

  return null
}
